import type { ReactNode } from 'react';
import { STATUS_LABEL, STATUS_TONE, type PillTone } from './format.js';

export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }): ReactNode {
  return <span className={`pill ${tone}`}>{children}</span>;
}

export function StatusPill({ status }: { status: string }): ReactNode {
  return <Pill tone={STATUS_TONE[status] ?? 'mute'}>{STATUS_LABEL[status] ?? status}</Pill>;
}

export function Card({
  title,
  action,
  children,
  padded = true,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}): ReactNode {
  return (
    <div className="card">
      {(title || action) && (
        <div className="hd">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {action}
        </div>
      )}
      {padded ? <div className="bd">{children}</div> : children}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'up' | 'down';
}): ReactNode {
  return (
    <div className="card stat">
      <span>{label}</span>
      <b className="num">{value}</b>
      {hint !== undefined && <small className={tone ?? ''}>{hint}</small>}
    </div>
  );
}

export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle !== undefined && <p>{subtitle}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

/**
 * Estado vazio com instrucao.
 *
 * Tela vazia sem explicacao gera chamado de suporte; a mensagem sempre diz o
 * que fazer em seguida.
 */
export function Empty({ title, children }: { title: string; children?: ReactNode }): ReactNode {
  return (
    <div className="empty">
      <b>{title}</b>
      {children}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div style={{ display: 'grid', gap: 10, padding: 20 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 18, width: `${100 - i * 8}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }): ReactNode {
  const message = error instanceof Error ? error.message : 'Nao foi possivel carregar os dados.';
  return (
    <div className="alert bad" style={{ margin: 20 }}>
      <b>Algo deu errado</b>
      {message}
      {onRetry && (
        <div className="fix">
          <button className="btn btn-sm btn-ghost" onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      )}
    </div>
  );
}

export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <div className={`overlay ${open ? 'on' : ''}`} onClick={onClose} />
      <aside className={`drawer ${open ? 'open' : ''}`} aria-hidden={!open} aria-label="Detalhe">
        <div className="dh">
          <div>
            <h2>{title}</h2>
            {subtitle !== undefined && <p>{subtitle}</p>}
          </div>
          <button className="x" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="db">{children}</div>
        {footer && <div className="df">{footer}</div>}
      </aside>
    </>
  );
}

/**
 * Grafico de barras dos ultimos dias.
 *
 * Mantido em CSS puro, sem biblioteca de grafico: sao catorze barras, e um
 * pacote de 300 KB para desenha-las tornaria a primeira tela mais lenta sem
 * ganho nenhum de leitura.
 */
export function Bars({
  data,
  labels,
}: {
  data: { day: string; value: number }[];
  labels?: [string, string, string];
}): ReactNode {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <>
      <div className="bars">
        {data.map((d, i) => (
          <i key={d.day} className={i === data.length - 1 ? 'today' : ''} title={`${d.day}: ${d.value}`}>
            <b style={{ height: `${Math.round((d.value / max) * 100)}%` }} />
          </i>
        ))}
      </div>
      {labels && (
        <div className="bar-labels">
          <span>{labels[0]}</span>
          <span>{labels[1]}</span>
          <span>{labels[2]}</span>
        </div>
      )}
    </>
  );
}
